// Layer 3 (Transformation): mine-side dispatch nudges from plant reads only.
// Pure — no React, no engine mutations.

import type { Case } from '@/versions/v1/schema/case';
import type { MineNudge } from '@/versions/v1/schema/mine-nudge';
import type { Plant } from '@/versions/v1/schema/plant';
import { INITIAL_ADVISORY_STATE, type AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import {
  PLANT,
  RECIRCULATING_LOOPS,
  STREAMS,
  SUBPROCESSES,
  UNIT_OPERATIONS,
  UTILIZATION_RANKS,
} from '@/versions/v1/shared/bunker-hill-seed';
import { LADDER_BY_RULE_ID } from '@/versions/v1/shared/bunker-hill-tarp-seed';
import { roleShortLabel } from '@/versions/v1/shared/org';
import { isHardOreZone, ORE_ZONES, oreZoneAt } from '@/versions/v1/shared/bunker-hill-ore-zones';
import {
  applyUtilization,
  bunkerHillUtilization,
  plantStrip,
} from '@/versions/v1/modules/ingestion/Scene.transform';
import { applyCases } from '@/versions/v1/shared/ScenarioEngine';
import {
  formatDeltaGrade,
  metOperatingCurves,
} from '@/versions/v1/shared/operating-curve.derive';
import { readingsAt } from '@/versions/v1/shared/series.derive';

const OPEN_CASE_STATES: readonly Case['state'][] = [
  'detected',
  'matched',
  'routed',
  'evidence-requested',
  'escalated',
  'diagnosed',
  'outcome-documented',
  'fmea-review',
];

function isOpenCase(caseItem: Case): boolean {
  return OPEN_CASE_STATES.includes(caseItem.state);
}

function cycloneInServiceClause(): string {
  const rank1 = UTILIZATION_RANKS[0];
  const cycRank = UTILIZATION_RANKS.find((row) => row.basis?.includes('in service'));
  const inServiceMatch = cycRank?.basis?.match(/^(\d+ of \d+) in service/);
  const cycloneFromRank1 = rank1?.basis?.match(/(\d+ of \d+) cyclones/);
  if (inServiceMatch) return `${inServiceMatch[1]} cyclones in service`;
  if (cycloneFromRank1) return `${cycloneFromRank1[1]} cyclones in service`;
  return '';
}

function parseStph(headline: string | null): number | undefined {
  if (!headline) return undefined;
  const match = headline.match(/^([\d.]+)\s*stph$/i);
  if (!match) return undefined;
  return Number.parseFloat(match[1]!);
}

function blendNudge(
  state: SessionState,
  variant: NonNullable<Plant['mineDispatch']>,
): MineNudge | undefined {
  if (!variant.blendApi) return undefined;
  const curves = metOperatingCurves(state, { omitFuture: true });
  const znCurve = curves.find((row) => row.metal === 'Zn');
  const zoneRows = znCurve?.curveJudgement?.zoneRows;
  if (!zoneRows?.length) return undefined;

  const currentZone = oreZoneAt(state.cursor);
  if (!currentZone) return undefined;

  const currentRow = zoneRows.find((row) => row.zoneId === currentZone.id);
  if (!currentRow || currentRow.meanDeltaGradePct >= 0) return undefined;

  const offPairShare = variant.offPairZoneSharePct;
  const otherZone = ORE_ZONES.find((zone) => zone.id !== currentZone.id);
  if (!otherZone) return undefined;
  const remainder = 100 - offPairShare;

  return {
    id: 'mine-nudge-blend',
    kind: 'blend',
    headline: `${currentZone.label} → ${offPairShare} % of feed · ${otherZone.label} ${remainder} %`,
    basis: `${currentRow.dayCount} d on the zone, mean grade ${formatDeltaGrade(currentRow.meanDeltaGradePct)} pts vs the pair`,
    target: 'to Komatsu dispatch',
    zoneId: currentZone.id,
    provenance: variant.provenance,
  };
}

function locationRateNudge(
  state: SessionState,
  advisory: AdvisorySessionState,
  variant: NonNullable<Plant['mineDispatch']>,
): MineNudge | undefined {
  if (!variant.locationRateApi) return undefined;

  const feedZone = oreZoneAt(state.cursor);
  if (!feedZone || !isHardOreZone(feedZone)) return undefined;

  const ranks = applyUtilization(bunkerHillUtilization(), 'idle', state);
  const strip = plantStrip(
    ranks,
    STREAMS,
    RECIRCULATING_LOOPS,
    SUBPROCESSES,
    UNIT_OPERATIONS,
    readingsAt(state),
  );
  const grindNode = strip.find((row) => row.id === 'sp-grind');
  if (!grindNode?.isBottleneck) return undefined;

  const cases = applyCases(CASES, state, advisory);
  const openCase =
    cases.find((row) => row.id === 'case-zn-recovery' && isOpenCase(row)) ??
    cases.find((row) => isOpenCase(row) && row.circuit.toLowerCase().includes('grind')) ??
    cases.find((row) => isOpenCase(row));
  if (!openCase?.code) return undefined;

  const fromStph = parseStph(grindNode.headline);
  if (fromStph === undefined) return undefined;

  const ladder = LADDER_BY_RULE_ID.get('dr-hardness');
  const feedTier = ladder?.tiers.find((tier) => tier.feedRateChangePct !== undefined);
  if (!feedTier || feedTier.feedRateChangePct === undefined) return undefined;
  const toStph = Math.round(fromStph * (1 + feedTier.feedRateChangePct / 100));

  const cycloneClause = cycloneInServiceClause();
  const basis = `TARP tier ${feedTier.tier} · ${roleShortLabel(feedTier.role)} · ${feedTier.timeLimitMinutes} m · ${cycloneClause}`;

  return {
    id: 'mine-nudge-location-rate',
    kind: 'location-rate',
    headline: `Grind feed ${fromStph} → ${toStph} stph while ${openCase.code} is open`,
    basis,
    target: 'to Komatsu dispatch',
    caseId: openCase.id,
    instructionTooltip: feedTier.instruction,
    provenance: variant.provenance,
  };
}

/** Mine-side dispatch nudges — empty when the plant seed omits `mineDispatch`. */
export function mineNudges(
  state: SessionState,
  advisory?: AdvisorySessionState,
  plant: Plant = PLANT,
): readonly MineNudge[] {
  const variant = plant.mineDispatch;
  if (!variant) return [];

  const advisoryState = advisory ?? INITIAL_ADVISORY_STATE;

  const nudges: MineNudge[] = [];
  const blend = blendNudge(state, variant);
  if (blend) nudges.push(blend);
  const locationRate = locationRateNudge(state, advisoryState, variant);
  if (locationRate) nudges.push(locationRate);
  return nudges;
}
